"use client";

import { ReactRenderer } from "@tiptap/react";
import tippy, { type Instance as TippyInstance } from "tippy.js";
import {
    forwardRef,
    useImperativeHandle,
    useState,
    useCallback,
} from "react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { searchWikiEntries } from "@/lib/actions/wiki-editor-actions";
import { CATEGORY_COLORS } from "@/lib/wiki-config";

// ─── Types ──────────────────────────────────────────────

interface MentionItem {
    id: string;
    name: string;
    category: string;
    categoryLabel: string;
}

interface MentionListRef {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

// ─── MentionList Component ──────────────────────────────

const MentionList = forwardRef<MentionListRef, SuggestionProps<MentionItem>>(
    (props, ref) => {
        const { items, command } = props;
        const [selectedIndex, setSelectedIndex] = useState(0);

        // Reset selected index when items change
        const [prevItems, setPrevItems] = useState(items);
        if (items !== prevItems) {
            setPrevItems(items);
            setSelectedIndex(0);
        }

        const selectItem = useCallback(
            (index: number) => {
                const item = items[index];
                if (item) {
                    command({ id: item.id, label: item.name });
                }
            },
            [items, command]
        );

        useImperativeHandle(ref, () => ({
            onKeyDown: ({ event }: { event: KeyboardEvent }) => {
                if (event.key === "ArrowUp") {
                    setSelectedIndex(
                        (prev) =>
                            (prev + items.length - 1) % items.length
                    );
                    return true;
                }
                if (event.key === "ArrowDown") {
                    setSelectedIndex(
                        (prev) => (prev + 1) % items.length
                    );
                    return true;
                }
                if (event.key === "Enter") {
                    selectItem(selectedIndex);
                    return true;
                }
                return false;
            },
        }));

        if (items.length === 0) {
            return (
                <div className="mention-dropdown">
                    <div className="mention-dropdown-empty">
                        No wiki entries found
                    </div>
                </div>
            );
        }

        return (
            <div className="mention-dropdown" role="listbox" id="mention-suggestions">
                {items.map((item, index) => {
                    const colors = CATEGORY_COLORS[item.category];
                    return (
                        <button
                            key={item.id}
                            type="button"
                            role="option"
                            aria-selected={index === selectedIndex}
                            className={`mention-dropdown-item ${index === selectedIndex
                                ? "mention-dropdown-item--active"
                                : ""
                                }`}
                            onClick={() => selectItem(index)}
                        >
                            <span
                                className={`mention-dropdown-category ${colors?.accent ?? "text-zinc-500"
                                    }`}
                            >
                                {item.categoryLabel}
                            </span>
                            <span className="mention-dropdown-name">
                                {item.name}
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    }
);

MentionList.displayName = "MentionList";

// ─── Suggestion Config ──────────────────────────────────

/** Lore search fn — injected so the demo can serve seed lore (defaults to the real action). */
type LoreSearchFn = (
    storyId: string,
    query: string
) => Promise<{ id: string; name: string; category: string; categoryLabel: string }[]>;

export function createMentionSuggestion(
    storyIdOrFn: string | (() => string),
    searchFn: LoreSearchFn = searchWikiEntries
): Omit<SuggestionOptions<MentionItem>, "editor"> {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    return {
        items: ({ query }) => {
            // Cancel any pending debounced search
            if (debounceTimer) {
                clearTimeout(debounceTimer);
                debounceTimer = null;
            }

            return new Promise<MentionItem[]>((resolve) => {
                const doSearch = async () => {
                    const storyId = typeof storyIdOrFn === "function" ? storyIdOrFn() : storyIdOrFn;
                    if (!storyId) return resolve([]);

                    try {
                        const results = await searchFn(storyId, query ?? "");
                        resolve(
                            results.map((r) => ({
                                id: r.id,
                                name: r.name,
                                category: r.category,
                                categoryLabel: r.categoryLabel,
                            }))
                        );
                    } catch {
                        resolve([]);
                    }
                };

                // No debounce for initial empty query (show entries immediately)
                // Debounce 300ms for typed queries to avoid per-keystroke calls
                const delay = !query ? 0 : 300;
                debounceTimer = setTimeout(doSearch, delay);
            });
        },

        render: () => {
            let component: ReactRenderer<MentionListRef> | null = null;
            let popup: TippyInstance[] | null = null;

            return {
                onStart: (props: SuggestionProps<MentionItem>) => {
                    component = new ReactRenderer(MentionList, {
                        props,
                        editor: props.editor,
                    });

                    if (!props.clientRect) return;

                    popup = tippy("body", {
                        getReferenceClientRect:
                            props.clientRect as () => DOMRect,
                        appendTo: () => document.body,
                        content: component.element,
                        showOnCreate: true,
                        interactive: true,
                        trigger: "manual",
                        placement: "bottom-start",
                    });
                },

                onUpdate: (props: SuggestionProps<MentionItem>) => {
                    component?.updateProps(props);

                    if (!props.clientRect) return;

                    popup?.[0]?.setProps({
                        getReferenceClientRect:
                            props.clientRect as () => DOMRect,
                    });
                },

                onKeyDown: (props: { event: KeyboardEvent }) => {
                    if (props.event.key === "Escape") {
                        popup?.[0]?.hide();
                        return true;
                    }

                    return (
                        component?.ref?.onKeyDown(props) ?? false
                    );
                },

                onExit: () => {
                    popup?.[0]?.destroy();
                    component?.destroy();
                },
            };
        },
    };
}
